import React from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import * as S from './CategorySelect.styles';
import { CategorySelectBox } from './components/CategorySelectBox';
import { CategorySelectDrawer } from './components/CategorySelectDrawer';
import { Popup } from '@/components/UI/Popup/Popup';
import { Select } from '@/components/UI/Select/Select';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Category } from '@/api/types/categories.types';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { AdFormFields } from '@/components/AdForm/AdForm.types';
import { useNewAdContext } from '@/contexts/NewAdContext';
import useScreenSize from '@/hooks/useScreenSize';

export const CategorySelect = () => {
  const { t } = useTranslation();
  const [isOpen, toggle] = useToggleOpenState();
  const { isLarge } = useScreenSize();
  const { control, setValue } = useFormContext<AdFormFields>();
  const {
    categories,
    selectedSubcategory,
    setSelectedSubcategory,
    setSelectedCategory,
  } = useNewAdContext();
  const {
    field: { name, value, onChange },
    fieldState,
  } = useController({
    name: 'categoryId',
    control,
  });

  const resetSelectedCategory = (subcategory: Category | null) => {
    const category = categories.find(
      ({ id }) => id === subcategory?.parent?.id
    );
    setSelectedCategory(category ?? null);
  };

  const handleSelectSubcategory = (subcategory: Category | null) => {
    setSelectedSubcategory(subcategory);
    resetSelectedCategory(subcategory);
    onChange(subcategory?.id ?? '');
    toggle(false);
    setValue('collection', {});
    setValue('range', {});
    setValue('boolean', {});
    setValue('photoIds', []);
    setValue('title', '');
  };

  const handleClose = () => {
    resetSelectedCategory(selectedSubcategory ?? null);
    toggle(false);
  };

  return (
    <Select
      id="lot_category_path"
      name={name}
      label={t('common.category')}
      isOpen={isOpen}
      toggle={toggle}
      value={value}
      visibleValue={value ? selectedSubcategory?.name : ''}
      icon={
        value &&
        selectedSubcategory?.parent?.icon && (
          <img
            alt=""
            src={selectedSubcategory?.parent?.icon}
            width={30}
            height={30}
            style={{ objectFit: 'contain' }}
            loading="lazy"
          />
        )
      }
      hasIcon
      hasBg
      isRequired
      appearance="filled"
      helperText={fieldState.error?.message}
      hasError={!!fieldState.error}
    >
      {isLarge ? (
        <Popup
          isOpen={isOpen}
          close={handleClose}
          customSize={{ width: 850, height: 800 }}
          variant="2"
        >
          <S.PopupContainer>
            <S.PopupHeader>
              <S.PopupTitle>{t('pages.new_ad.new_ad')}</S.PopupTitle>
              <S.PopupCloseButton
                data-testid="popup-close-button"
                appearance="transparent"
                hasPadding={false}
                onClick={handleClose}
              >
                <CloseIcon width={20} height={20} />
              </S.PopupCloseButton>
            </S.PopupHeader>
            <CategorySelectBox
              isOpen={isOpen}
              onSelectSubcategory={handleSelectSubcategory}
            />
          </S.PopupContainer>
        </Popup>
      ) : (
        <CategorySelectDrawer
          isOpen={isOpen}
          onClose={handleClose}
          onSelectSubcategory={handleSelectSubcategory}
        />
      )}
    </Select>
  );
};
